// src/pages/dashboard/MenuOrdersEntry.jsx
// Entrée "Commandes du menu" : trouve les menus de l'utilisateur puis ouvre
// /app/menus/:id/orders (directement s'il n'y a qu'un seul menu).
// Style luxe : sobre, filets dorés, titres serif, cohérent avec le dashboard.

import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  ArrowRight,
  Plus,
  AlertCircle,
  RefreshCw,
  X,
  UtensilsCrossed,
  GripVertical
} from 'lucide-react'
import api from '../../lib/axios'

const ORDER_KEY = 'menu-orders-card-order'

const loadOrder = () => {
  try {
    const v = JSON.parse(localStorage.getItem(ORDER_KEY) || '[]')
    return Array.isArray(v) ? v : []
  } catch {
    return []
  }
}

const saveOrder = (ids) => {
  try {
    localStorage.setItem(ORDER_KEY, JSON.stringify(ids))
  } catch {
    /* stockage indisponible : l'ordre ne sera pas mémorisé */
  }
}

// Applique l'ordre sauvegardé ; les nouveaux menus vont à la fin.
const applyOrder = (list, ids) => {
  const rank = new Map(ids.map((id, i) => [String(id), i]))
  return [...list].sort(
    (a, b) =>
      (rank.has(String(a.id)) ? rank.get(String(a.id)) : 9999) -
      (rank.has(String(b.id)) ? rank.get(String(b.id)) : 9999)
  )
}

const parseSettings = (raw) => {
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) || {}
    } catch {
      return {}
    }
  }
  return raw && typeof raw === 'object' ? raw : {}
}

const isMenuPage = (p) => {
  const s = parseSettings(p.settings)
  return p.type === 'menu' || Array.isArray(s.categories)
}

const getTitle = (p) => p.title || p.name || `Menu ${p.id}`

const getStats = (p) => {
  const s = parseSettings(p.settings)
  if (!Array.isArray(s.categories)) return null
  return {
    categories: s.categories.length,
    items: s.categories.reduce(
      (n, c) => n + (Array.isArray(c.items) ? c.items.length : 0),
      0
    )
  }
}

const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`

/* ------------------------------------------------------------------ */
/*  Éléments d'interface                                               */
/* ------------------------------------------------------------------ */

function GoldRule({ className = '' }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="h-px w-10 bg-gradient-to-r from-gold to-gold/0" />
      <span className="w-1.5 h-1.5 rotate-45 bg-gold" />
      <span className="h-px w-10 bg-gradient-to-l from-gold to-gold/0" />
    </div>
  )
}

function PageShell({ count, children }) {
  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto">
      <header className="relative text-center pt-6 pb-10 mb-10 border-b border-gray-100">
        <p className="text-[11px] uppercase tracking-[0.35em] text-gold font-semibold">
          Service en salle
        </p>

        <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
          Commandes du menu
        </h1>

        <GoldRule className="justify-center mt-6" />

        <p className="mt-6 text-sm sm:text-base text-gray-500 max-w-xl mx-auto leading-relaxed">
          Suivez chaque commande et chaque demande de vos clients,
          en temps réel, avec élégance.
        </p>

        {typeof count === 'number' && count > 0 && (
          <div className="mt-6 inline-flex items-center gap-2 text-xs text-gray-500">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            <span className="tracking-wide">
              {plural(count, 'menu')} connecté{count > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </header>

      {children}
    </div>
  )
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-8 animate-pulse">
      <div className="w-12 h-12 rounded-full bg-gray-100 mx-auto mb-6" />
      <div className="h-5 w-1/2 bg-gray-100 rounded mx-auto mb-4" />
      <div className="h-3 w-2/3 bg-gray-100 rounded mx-auto mb-8" />
      <div className="h-px bg-gray-100 mb-5" />
      <div className="h-3 w-1/3 bg-gray-100 rounded mx-auto" />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function MenuOrdersEntry() {
  const navigate = useNavigate()
  const [pages, setPages] = useState(null)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [dragId, setDragId] = useState(null)
  const [overId, setOverId] = useState(null)

  const load = useCallback(() => {
    let alive = true
    setError('')
    setPages(null)

    api
      .get('/pages')
      .then((res) => {
        const list = res.data?.data ?? res.data ?? []
        const menus = (Array.isArray(list) ? list : []).filter(isMenuPage)
        if (!alive) return
        if (menus.length === 1) {
          navigate(`/app/menus/${menus[0].id}/orders`, { replace: true })
        } else {
          setPages(applyOrder(menus, loadOrder()))
        }
      })
      .catch(() => alive && setError('Impossible de charger vos menus.'))

    return () => {
      alive = false
    }
  }, [navigate])

  useEffect(() => load(), [load])

  const filtered = useMemo(() => {
    if (!pages) return []
    const q = query.trim().toLowerCase()
    if (!q) return pages
    return pages.filter((p) => getTitle(p).toLowerCase().includes(q))
  }, [pages, query])

  const canDrag = !query.trim()

  const handleDrop = (targetId) => {
    if (dragId == null || String(dragId) === String(targetId)) return
    setPages((prev) => {
      const from = prev.findIndex((p) => String(p.id) === String(dragId))
      const to = prev.findIndex((p) => String(p.id) === String(targetId))
      if (from < 0 || to < 0) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      saveOrder(next.map((p) => p.id))
      return next
    })
  }

  const endDrag = () => {
    setDragId(null)
    setOverId(null)
  }

  // ---- Erreur
  if (error) {
    return (
      <PageShell>
        <div className="rounded-2xl border border-gray-100 bg-white p-12 text-center shadow-sm">
          <div className="w-14 h-14 mx-auto mb-6 rounded-full border border-red-100 bg-red-50 text-red-500 flex items-center justify-center">
            <AlertCircle className="w-6 h-6" />
          </div>
          <p className="font-serif text-xl text-navy mb-2">{error}</p>
          <p className="text-sm text-gray-500 mb-8">
            Vérifiez votre connexion puis réessayez.
          </p>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-xs font-semibold uppercase tracking-[0.18em] hover:bg-navy/90 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Réessayer
          </button>
        </div>
      </PageShell>
    )
  }

  // ---- Chargement
  if (!pages) {
    return (
      <PageShell>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </PageShell>
    )
  }

  // ---- Aucun menu
  if (pages.length === 0) {
    return (
      <PageShell>
        <div className="rounded-2xl border border-gray-100 bg-white p-14 text-center shadow-sm">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-gold/40 text-gold flex items-center justify-center">
            <UtensilsCrossed className="w-7 h-7" />
          </div>
          <h2 className="font-serif text-2xl text-navy mb-3">
            Aucun menu pour le moment
          </h2>
          <GoldRule className="justify-center mb-5" />
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-10 leading-relaxed">
            Créez votre premier menu digital pour commencer à recevoir et suivre
            les commandes de vos clients.
          </p>
          <button
            onClick={() => navigate('/app/menus/new')}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-navy text-white text-xs font-semibold uppercase tracking-[0.18em] hover:bg-navy/90 transition"
          >
            <Plus className="w-4 h-4" />
            Créer un menu
          </button>
        </div>
      </PageShell>
    )
  }

  // ---- Liste des menus
  return (
    <PageShell count={pages.length}>
      {pages.length > 3 && (
        <div className="relative max-w-md mx-auto mb-10">
          <Search className="w-4 h-4 text-gray-400 absolute left-5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un menu"
            className="w-full pl-12 pr-11 py-3.5 rounded-full border border-gray-200 bg-white text-sm focus:outline-none focus:border-gold focus:ring-4 focus:ring-gold/15 transition"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-navy"
              aria-label="Effacer la recherche"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-16">
          Aucun menu ne correspond à « {query} ».
        </p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p, i) => {
            const stats = getStats(p)
            return (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                draggable={canDrag}
                onClick={() => navigate(`/app/menus/${p.id}/orders`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    navigate(`/app/menus/${p.id}/orders`)
                  }
                }}
                onDragStart={(e) => {
                  setDragId(p.id)
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', String(p.id))
                }}
                onDragOver={(e) => {
                  if (dragId == null) return
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  if (overId !== p.id) setOverId(p.id)
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  handleDrop(p.id)
                  endDrag()
                }}
                onDragEnd={endDrag}
                className={`group relative text-center select-none rounded-2xl border bg-white px-8 pt-10 pb-7 shadow-sm hover:shadow-2xl hover:shadow-navy/10 hover:-translate-y-1 hover:border-gold/50 transition-all duration-500 focus:outline-none focus:ring-4 focus:ring-gold/20 ${
                  canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                } ${
                  String(dragId) === String(p.id)
                    ? 'opacity-40 scale-95 border-gold'
                    : overId === p.id && dragId != null
                    ? 'border-gold ring-4 ring-gold/20 -translate-y-1'
                    : 'border-gray-100'
                }`}
              >
                {/* filet doré supérieur */}
                <span className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-0 group-hover:w-2/3 bg-gradient-to-r from-transparent via-gold to-transparent transition-all duration-500" />

                {/* poignée de déplacement */}
                {canDrag && (
                  <span
                    className="absolute top-3.5 right-4 text-gray-300 group-hover:text-gold transition-colors"
                    title="Glisser pour réorganiser"
                  >
                    <GripVertical className="w-4 h-4" />
                  </span>
                )}

                {/* numéro */}
                <span className="absolute top-4 left-5 font-serif text-xs text-gray-300 tabular-nums">
                  {String(i + 1).padStart(2, '0')}
                </span>

                <div className="w-14 h-14 mx-auto rounded-full border border-gold/40 text-gold flex items-center justify-center group-hover:bg-navy group-hover:border-navy group-hover:text-gold transition-colors duration-500">
                  <UtensilsCrossed className="w-6 h-6" />
                </div>

                <h3 className="mt-6 font-serif text-xl text-navy truncate">
                  {getTitle(p)}
                </h3>

                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-gray-400 min-h-[16px]">
                  {stats
                    ? `${plural(stats.items, 'plat')} · ${plural(
                        stats.categories,
                        'catégorie'
                      )}`
                    : 'Menu digital'}
                </p>

                <div className="mt-7 pt-5 border-t border-gray-100 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy group-hover:text-gold transition-colors duration-300">
                  Voir les commandes
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </PageShell>
  )
}